import { cloneDeep } from 'lodash';

import { FlowType } from '@ply-io/ply-front-shared-utilities';

import { MODULE_NAME } from './const';
import {
	editWorkflow,
	element,
	getWorkflowAppearanceSettings,
	isNativeElement,
	processWorkflowIds as processWorkflowIdsGetter,
	subflowNodes,
} from './getters';
import { SET_CONTEXTUAL_LOADING, SET_PUBLISH_LOADING, SET_SELECTED_WORKFLOW_ID, SET_IS_INTERFACE_PREVIEW_ENABLED } from './mutations';
import state from './state';
import StoreModuleService from '../StoreModuleService';
import { setConfigurationsValues } from '../configurationEditor/actions';
import { handleElementUpdated } from '../elements/actions';
import { isInterfaceFocusedLayoutModeEnabled } from '../featureFlags/getters';
import { setLeftSideDrawerState } from '../leftSideDrawer/actions';
import {
	handleGraphAffectedChanges,
	duplicateStep as duplicateStepAction,
	deleteNode as deleteNodeAction,
	getNodeData,
	handleNodeUpdated,
	handleNodesUpdated,
} from '../nodes/actions';
import { addWorkflowIdToProject } from '../projects/actions';
import { closeSideBar, clearEditNode, setSideBarState } from '../selectedNode/actions';
import { editNode } from '../selectedNode/getters';
import { selectedProjectId } from '../selectedProject/getters';
import { subflowIdFromNode } from '../subflows/getters';
import {
	createInstallableForWorkflow,
	createModule,
	createWorkflow,
	createSubflowFromExistingNodes,
	getWorkflowData,
	publishWorkflow as publishWorkflowAction,
	restoreWorkflowVersionAsDraft,
	setAllWorkflowData,
	updateWorkflow as updateWorkflowAction,
	setWorkflowShareData,
	fetchIsWaitingForSampleData,
	updateWorkflowDraftPendingChangesFlag,
} from '../workflows/actions';
import api from '@/api';
import { useFlowLayoutMode } from '@/components/FlowTree/composables';
import router from '@/router';
import { BLOCK_EDITOR_ROUTE_NAME } from '@/router/router.const';
import { getActionsOfApp } from '@/store/modules/apps/actions';
import { rootNode } from '@/store/modules/selectedWorkflow/getters';
import { nodesDataArray } from '@/store/modules/workflows/getters';
import pusherService, { PUSHER_USAGE_TYPES } from '@/utils/pusher';

const buildSelectedWorkflowActionObject = (action) => StoreModuleService.buildActionObject(MODULE_NAME, action);

// actions declarations
export const updateWorkflowSetting = buildSelectedWorkflowActionObject('updateWorkflowSetting');
export const partialUpdateWorkflowVersionSettings = buildSelectedWorkflowActionObject('partialUpdateWorkflowVersionSettings');
export const saveWorkflowSettings = buildSelectedWorkflowActionObject('saveWorkflowSettings');
export const saveWorkflowScheduledTriggerConfiguration = buildSelectedWorkflowActionObject('saveWorkflowScheduledTriggerConfiguration');
export const selectWorkflowId = buildSelectedWorkflowActionObject('selectWorkflowId');
export const createAndSelectWorkflow = buildSelectedWorkflowActionObject('createAndSelectWorkflow');
export const createAndSelectWorkflowModule = buildSelectedWorkflowActionObject('createAndSelectWorkflowModule');
export const reloadSelectedWorkflow = buildSelectedWorkflowActionObject('reloadSelectedWorkflow');
export const createWorkflowPusherConnection = buildSelectedWorkflowActionObject('createWorkflowPusherConnection');
export const removeWorkflowPusherConnection = buildSelectedWorkflowActionObject('removeWorkflowPusherConnection');
export const setContextualDataState = buildSelectedWorkflowActionObject('setContextualDataState');
export const setContextualDataProfileID = buildSelectedWorkflowActionObject('setContextualDataProfileID');
export const updateWorkflow = buildSelectedWorkflowActionObject('updateWorkflow');
export const immediateUpdateWorkflow = buildSelectedWorkflowActionObject('immediateUpdateWorkflow');
export const publishWorkflow = buildSelectedWorkflowActionObject('publishWorkflow');
export const restoreSelectedWorkflowSpecificVersionAsDraft = buildSelectedWorkflowActionObject(
	'restoreSelectedWorkflowSpecificVersionAsDraft'
);
export const createInstallableForSelectedWorkflow = buildSelectedWorkflowActionObject('createInstallableForSelectedWorkflow');
export const setPublishLoading = buildSelectedWorkflowActionObject('setPublishLoading');
export const setIsInterfacePreviewEnabled = buildSelectedWorkflowActionObject('setIsInterfacePreviewEnabled');
export const enterTriggerMode = buildSelectedWorkflowActionObject('enterTriggerMode');
export const duplicateStep = buildSelectedWorkflowActionObject('duplicateStep');
export const updateWorkflowVersionTrigger = buildSelectedWorkflowActionObject('updateWorkflowVersionTrigger');
export const fetchSharedData = buildSelectedWorkflowActionObject('fetchSharedData');
export const deleteNode = buildSelectedWorkflowActionObject('deleteNode');
export const closeSideBarIfEditNodeDeleted = buildSelectedWorkflowActionObject('closeSideBarIfEditNodeDeleted');
export const createSubflow = buildSelectedWorkflowActionObject('createSubflow');
export const convertToSubflow = buildSelectedWorkflowActionObject('convertToSubflow');
export const updateDraftPendingChangesFlag = buildSelectedWorkflowActionObject('updateDraftPendingChangesFlag');
export const reFetchRootNode = buildSelectedWorkflowActionObject('reFetchRootNode');

const actions = {
	...StoreModuleService.createDefaultActions(),
	async [selectWorkflowId.action]({ commit, dispatch, getters, rootGetters }, payload) {
		const { id } = payload;
		commit(SET_SELECTED_WORKFLOW_ID, id);
		if (id) {
			await dispatch(setConfigurationsValues.fullPath, {}, { root: true });
			await dispatch(
				getWorkflowData.fullPath,
				{
					id,
					forceGet: true,
					forceGetNodes: true,
					dontGetConfigurations: false,
					getSubFlowConfigurationParams: true,
					getWorkflowWaitingFlag: true,
				},
				{ root: true }
			);
			dispatch(createWorkflowPusherConnection.action); // call pusher after these calls above so we are sure we refreshed the credentials token at this point

			// When editing a workflow that's in a process, let's retrieve all the process' workflows.
			const processWorkflowIds = getters[processWorkflowIdsGetter.getter];
			if (processWorkflowIds?.length) {
				// We gonna load process workflows and actions from server,
				// so data input menu (and others) have the info they need.
				await Promise.all(
					processWorkflowIds.map(async (id) => {
						await dispatch(
							getWorkflowData.fullPath,
							{
								id,
								forceGet: true,
								forceGetNodes: true,
								dontGetConfigurations: false,
								getSubFlowConfigurationParams: true,
								getWorkflowWaitingFlag: true,
							},
							{ root: true }
						);
					})
				);
			}

			const apps = new Set();
			rootGetters[nodesDataArray.fullPath](id).map(({ service, service_version }) => apps.add({ id: service, version: service_version }));
			await Promise.all(Array.from(apps).map((appPayload) => dispatch(getActionsOfApp.fullPath, appPayload, { root: true })));

			const selectedWorkflowSubflowNodes = getters[subflowNodes.getter];
			if (selectedWorkflowSubflowNodes.length) {
				await Promise.all(
					selectedWorkflowSubflowNodes.map(async (node) => {
						const subflowId = rootGetters[subflowIdFromNode.fullPath](node);
						if (!subflowId) {
							return;
						}
						await dispatch(
							getWorkflowData.fullPath,
							{ id: subflowId, forceGet: true, getSubFlowConfigurationParams: true, getWorkflowWaitingFlag: true },
							{ root: true }
						);
					})
				);
			}
		} else {
			console.log('no flow data. redirecting');
			router.push({ name: 'Project' });
		}
	},
	async [createAndSelectWorkflow.action]({ dispatch, rootGetters }, payload) {
		const { workflow, selectFirstNonRoot } = payload;
		const projectId = workflow.project || rootGetters[selectedProjectId.fullPath];
		const newWorkflow = await dispatch(
			createWorkflow.fullPath,
			{
				workflow: Object.assign({}, workflow, { project: projectId }),
			},
			{ root: true }
		);
		let nodeIdToSelect;
		if (selectFirstNonRoot) {
			const wfVer = newWorkflow.current_draft;
			nodeIdToSelect = wfVer.nodes.find((node) => node !== wfVer.root_node);
		}
		let routeName;
		const routeParams = { wfID: newWorkflow.id, pID: newWorkflow.project };
		if (nodeIdToSelect) {
			routeName = BLOCK_EDITOR_ROUTE_NAME;
			routeParams.nodeID = nodeIdToSelect;
		} else {
			routeName = 'Feature';
		}
		await Promise.all([
			dispatch(addWorkflowIdToProject.fullPath, { projectId: newWorkflow.project, workflowId: newWorkflow.id }, { root: true }),
			router.push({ name: routeName, params: routeParams }, (r) => {
				if (nodeIdToSelect && rootGetters[isInterfaceFocusedLayoutModeEnabled.fullPath]) {
					const { isNormalMode } = useFlowLayoutMode(router, r);
					isNormalMode.value = false;
				}
			}),
		]);
		return newWorkflow;
	},
	async [createAndSelectWorkflowModule.action]({ dispatch, rootGetters }, payload) {
		const { module, moduleType } = payload;
		const newWorkflow = await dispatch(
			createModule.fullPath,
			{ module: Object.assign({}, module, { projectId: rootGetters[selectedProjectId.fullPath] }), moduleType },
			{ root: true }
		);
		dispatch(closeSideBar.fullPath, null, { root: true });
		dispatch(
			addWorkflowIdToProject.fullPath,
			{ projectId: rootGetters[selectedProjectId.fullPath], workflowId: newWorkflow.id, forceGet: true, forceGetNodes: true },
			{ root: true }
		);
		router.push({ name: 'Feature', params: { wfID: newWorkflow.id, pID: rootGetters[selectedProjectId.fullPath], elementEditor: true } });
	},
	[reloadSelectedWorkflow.action]({ dispatch }, payload) {
		const { forceGetNodes } = payload;
		if (state.selectedWorkflowId) {
			dispatch(getWorkflowData.fullPath, { id: state.selectedWorkflowId, forceGet: true, forceGetNodes }, { root: true });
		}
	},
	async [updateDraftPendingChangesFlag.action]({ dispatch }, { value }) {
		const workflowId = state.selectedWorkflowId;
		if (workflowId) {
			return await dispatch(updateWorkflowDraftPendingChangesFlag.fullPath, { value, workflowId }, { root: true });
		}
	},

	// TODO - this is the only logic remained in selected workflow instead of workflows itself
	async [setContextualDataState.action]({ commit, state, dispatch }, payload) {
		const { state: enabled } = payload;
		commit(SET_CONTEXTUAL_LOADING, true);
		const changes = await api.setContextualStepState(state.selectedWorkflowId, enabled);
		await dispatch(handleGraphAffectedChanges.fullPath, { changes }, { root: true });
		commit(SET_CONTEXTUAL_LOADING, false);
	},
	// TODO - this is the only logic remained in selected workflow instead of workflows itself
	async [setContextualDataProfileID.action]({ getters }, payload) {
		const { credentials_configuration } = payload;
		const method = getters[isNativeElement.getter] ? api.setNativeElementContextualDataProfileID : api.setUiElementContextualDataProfileID;
		await method(getters[element.getter].id, credentials_configuration);
	},
	async [updateWorkflow.action]({ dispatch }, payload) {
		const { data } = payload;
		return await dispatch(updateWorkflowAction.fullPath, { id: state.selectedWorkflowId, data }, { root: true });
	},
	[immediateUpdateWorkflow.action]({ dispatch }, payload) {
		const { data } = payload;
		return dispatch(updateWorkflowAction.fullPath, { id: state.selectedWorkflowId, data }, { root: true });
	},
	[publishWorkflow.action]({ dispatch }, payload) {
		return dispatch(publishWorkflowAction.fullPath, { ...payload, id: state.selectedWorkflowId }, { root: true });
	},
	[createWorkflowPusherConnection.action]({ dispatch }) {
		const workflowId = Number(state.selectedWorkflowId);
		const pusherEvents = [
			pusherService.createPusherEventObj(
				'draft-pending-changes-flag-updated',
				({ draft_pending_changes_flag }) => dispatch(updateDraftPendingChangesFlag.action, { value: draft_pending_changes_flag }),
				PUSHER_USAGE_TYPES.WORKFLOW
			),
			pusherService.createPusherEventObj(
				'element-updated',
				(data) => dispatch(handleElementUpdated.fullPath, data, { root: true }),
				PUSHER_USAGE_TYPES.WORKFLOW
			),
			pusherService.createPusherEventObj(
				'workflow-waiting-flag-updated',
				(data) => dispatch(fetchIsWaitingForSampleData.fullPath, { ...data }, { root: true }),
				PUSHER_USAGE_TYPES.WORKFLOW
			),
			pusherService.createPusherEventObj('workflow-root-node-updated', () => dispatch(reFetchRootNode.action), PUSHER_USAGE_TYPES.WORKFLOW),
			pusherService.createPusherEventObj(
				'workflow-node-test-result-updated',
				(data) => dispatch(handleNodeUpdated.fullPath, { pk: data.node_pk }, { root: true }),
				PUSHER_USAGE_TYPES.WORKFLOW
			),
			pusherService.createPusherEventObj(
				'workflow-node-params-map-updated',
				(data) => dispatch(handleNodesUpdated.fullPath, { nodeIds: data.node_ids }, { root: true }),
				PUSHER_USAGE_TYPES.WORKFLOW
			),
		];

		pusherService.listenToEvents(pusherEvents, workflowId);
		pusherService.subscribeToWorkflow(workflowId);
	},
	[removeWorkflowPusherConnection.action]() {
		pusherService.clearWorkflowChannel(false);
	},
	[restoreSelectedWorkflowSpecificVersionAsDraft.action]({ dispatch }, { version, copyConfigurationsResponseFromVersion }) {
		return dispatch(
			restoreWorkflowVersionAsDraft.fullPath,
			{ workflowId: state.selectedWorkflowId, version, copyConfigurationsResponseFromVersion },
			{ root: true }
		);
	},

	[createInstallableForSelectedWorkflow.action]({ dispatch }) {
		return dispatch(createInstallableForWorkflow.fullPath, { id: state.selectedWorkflowId }, { root: true });
	},
	[setPublishLoading.action]({ commit }, payload) {
		commit(SET_PUBLISH_LOADING, payload);
	},
	async [saveWorkflowSettings.action]({ getters, dispatch }, payload) {
		const newSettings = payload.settings;
		const currentWorkflow = getters[editWorkflow.getter];
		const clonedWorkflow = cloneDeep(currentWorkflow);
		const newCurrentDraft = { ...clonedWorkflow.current_draft, settings: newSettings };
		const newWorkflow = { ...clonedWorkflow, current_draft: newCurrentDraft };
		return dispatch(setAllWorkflowData.fullPath, { data: newWorkflow }, { root: true });
	},
	async [saveWorkflowScheduledTriggerConfiguration.action]({ getters, dispatch }, payload) {
		const newConfig = payload.scheduled_trigger_config_version;
		const currentWorkflow = getters[editWorkflow.getter];
		const clonedWorkflow = cloneDeep(currentWorkflow);
		const newCurrentDraft = { ...clonedWorkflow.current_draft, scheduled_trigger_config_version: newConfig };
		const newWorkflow = { ...clonedWorkflow, current_draft: newCurrentDraft };
		return dispatch(setAllWorkflowData.fullPath, { data: newWorkflow }, { root: true });
	},
	async [updateWorkflowSetting.action]({ getters, dispatch }, payload) {
		const settingKey = payload.key;
		const settingVal = payload.value;
		const workflowSettings = getters[getWorkflowAppearanceSettings.getter];
		const clonedSettings = cloneDeep(workflowSettings);
		const settings = { ...clonedSettings, [settingKey]: settingVal };
		const workflowSettingsId = getters[getWorkflowAppearanceSettings.getter].id;
		await api.saveWorkflowSettings(workflowSettingsId, { [settingKey]: settingVal });
		return dispatch(saveWorkflowSettings.action, { settings });
	},
	async [partialUpdateWorkflowVersionSettings.action]({ getters, dispatch }, fields) {
		const workflowVersionSettingsId = getters[getWorkflowAppearanceSettings.getter].id;
		const settings = await api.saveWorkflowSettings(workflowVersionSettingsId, fields);
		return dispatch(saveWorkflowSettings.action, { settings });
	},
	async [enterTriggerMode.action]({ dispatch }) {
		await dispatch(clearEditNode.fullPath, null, { root: true });
		await dispatch(setLeftSideDrawerState.fullPath, null, { root: true });
		await dispatch(setSideBarState.fullPath, { sideBarState: 'trigger' }, { root: true });
	},
	[setIsInterfacePreviewEnabled.action]({ commit }, payload) {
		commit(SET_IS_INTERFACE_PREVIEW_ENABLED, payload);
	},
	async [duplicateStep.action]({ dispatch }, { nodeId }) {
		await dispatch(duplicateStepAction.fullPath, { nodeId, workflowId: state.selectedWorkflowId }, { root: true });
	},
	async [updateWorkflowVersionTrigger.action]({ dispatch }, payload) {
		const { workflowId, workflowVersionId, config } = payload;
		const ret = await api.updateWorkflowVersionTrigger(workflowId, workflowVersionId, config);
		await dispatch(saveWorkflowScheduledTriggerConfiguration.action, { scheduled_trigger_config_version: ret });
		return ret;
	},
	[fetchSharedData.action]({ dispatch }, payload) {
		return dispatch(setWorkflowShareData.fullPath, { id: state.selectedWorkflowId, ...payload }, { root: true });
	},
	async [deleteNode.action]({ dispatch }, { id: nodeId }) {
		await dispatch(closeSideBarIfEditNodeDeleted.action, { id: nodeId });
		const ret = await dispatch(deleteNodeAction.fullPath, { nodeId, workflowId: state.selectedWorkflowId }, { root: true });
		return ret;
	},
	[closeSideBarIfEditNodeDeleted.action]({ dispatch, rootGetters }, payload) {
		const { id: nodeId } = payload;
		const editNodeObject = rootGetters[editNode.fullPath];
		if (editNodeObject?.id === nodeId) {
			return dispatch(closeSideBar.fullPath, {}, { root: true });
		}
	},
	async [createSubflow.action]({ dispatch }, { projectId, subflowName }) {
		const newWorkflow = await dispatch(
			createWorkflow.fullPath,
			{ workflow: { project: projectId, flow_type: FlowType.Sub, workflow_title: subflowName } },
			{ root: true }
		);
		await dispatch(addWorkflowIdToProject.fullPath, { projectId: newWorkflow.project, workflowId: newWorkflow.id }, { root: true });
		return newWorkflow;
	},
	async [convertToSubflow.action]({ dispatch, getters }, { subflowName, startNodeId, endNodeId }) {
		const currentWorkflow = getters[editWorkflow.getter];
		const result = await dispatch(
			createSubflowFromExistingNodes.fullPath,
			{ workflow_id: currentWorkflow.id, start_node_id: Number(startNodeId), end_node_id: Number(endNodeId), sub_flow_name: subflowName },
			{ root: true }
		);
		const { old_workflow_id: oldWorkflowId, new_workflow_id: newWorkflowId } = result;
		await Promise.all([
			dispatch(getWorkflowData.fullPath, { id: oldWorkflowId, forceGet: true, forceGetNodes: true }, { root: true }),
			async () => {
				const workflow = await dispatch(
					getWorkflowData.fullPath,
					{ id: newWorkflowId, forceGet: true, forceGetNodes: true },
					{ root: true }
				);
				await dispatch(addWorkflowIdToProject.fullPath, { projectId: workflow.project, workflowId: newWorkflowId }, { root: true });
			},
		]);
		return result;
	},
	async [reFetchRootNode.action]({ dispatch, rootGetters }) {
		const rootNodeId = rootGetters[rootNode.fullPath].id;
		return dispatch(getNodeData.fullPath, { id: rootNodeId, forceGet: true }, { root: true });
	},
};

export default actions;
